/**
 * One frame of 3D COCKPIT's interior treatment: the hour's sun, and whether the
 * guns just went off.
 *
 * The arithmetic is `cockpit-interior.ts`'s and the uniforms are
 * `three/cockpit-interior.ts`'s; this is the dozen lines that read the scene's
 * own state and hand one to the other, in the same `scene-*` shape
 * `scene-propellers.ts` and `scene-shadows.ts` already have. It lives apart from
 * `scene-loop.ts` because it is the only code in the frame that needs the gun
 * spec *and* the mission's hour *and* the painted breeches, and gathering those
 * three inside `onFrame` is what would push that function past its ceiling.
 *
 * **Nothing here can reach the simulation.** It reads `SimState` and writes
 * uniforms — the same "the feature is paint" property `cockpit-3d.test.ts`
 * asserts for the rest of 3D COCKPIT, and what lets `/vcr` play a tape back with
 * the setting on and stay bit-identical.
 */

import type { Pose, SimState, TimeOfDay, Vec3 } from "@red-opus/engine";
import type { CockpitInterior } from "../three/cockpit-interior.ts";
import { advanceMuzzleFlash, muzzleFlashStrength, type MuzzleFlashState } from "./cockpit-interior.ts";
import { DEFAULT_ENHANCED_HOUR } from "./enhanced-sky-look.ts";
import { sunDirectionAt } from "./time-of-day.ts";

/** The slice of the scene this needs — structural, like every other `scene-*` host here. */
export interface CockpitInteriorHost {
  readonly cockpitInterior: CockpitInterior;
  readonly muzzleFlash: MuzzleFlashState;
  /** The mission's briefed hour, or `undefined` for a dogfight or duel that briefs none. */
  readonly timeOfDay: TimeOfDay | undefined;
}

export interface CockpitInteriorFrameInput {
  readonly dtS: number;
  readonly pose: Pose;
  readonly simState: SimState;
  /** Whether the player is in his own 3D cockpit — `scene-camera.ts`'s `dashQuadVisible`, off the same subject. */
  readonly inCockpit3d: boolean;
  /**
   * The painted gun breeches as world deltas from the pose origin, exactly as
   * `GunMuzzles.breechOffsets` returns them and the drawn rounds use them.
   *
   * The *painted* breech rather than the sim's barrel, because the flash the
   * player sees is the one on the dash quad and this is the light that flash
   * casts. They differ by centimetres in the ordinary case (`gun-muzzles.ts`
   * pins the shift at zero when no cockpit art is loaded) and by a screen
   * registration's worth when a cockpit is loaded, which is exactly the
   * difference that should move the light.
   */
  readonly breechWorldOffsets: readonly Vec3[];
  /** Rounds per second per gun — `GunSpec.roundsPerSecond`, so the flash's lifetime is the gun's own cadence. */
  readonly roundsPerSecond: number;
}

/**
 * The flash's world position: the mean of the painted breeches.
 *
 * One point for a pair of guns, and the mean is the honest reduction — twin
 * Vickers sit about 30 cm apart on a Camel's decking and the structure they
 * light is a metre away, so two sources and their average differ by less than
 * the width of one shade rung at every distance the falloff is doing anything
 * at. A second uniform and a second `distance` in the fragment shader would buy
 * a difference nothing can see.
 */
function meanOffset(offsets: readonly Vec3[]): Vec3 | undefined {
  if (offsets.length === 0) return undefined;
  let x = 0;
  let y = 0;
  let z = 0;
  for (const offset of offsets) {
    x += offset.x;
    y += offset.y;
    z += offset.z;
  }
  return { x: x / offsets.length, y: y / offsets.length, z: z / offsets.length };
}

export function updateCockpitInterior(host: CockpitInteriorHost, frame: CockpitInteriorFrameInput): void {
  // Advanced every frame, in or out of the cockpit: the timer is about the gun,
  // not about the camera, so switching to a chase view mid-burst and back must
  // not find the flash frozen where it was left.
  advanceMuzzleFlash(host.muzzleFlash, { dtS: frame.dtS, burstRounds: frame.simState.guns.burstRounds });
  const muzzle = meanOffset(frame.breechWorldOffsets);
  host.cockpitInterior.update({
    enabled: frame.inCockpit3d,
    // `timeOfDay ?? DEFAULT_ENHANCED_HOUR`, the same fallback `ground-shadow.ts`
    // takes and for the same reason, discovered the same way — by looking at
    // it. A dogfight and a duel brief no weather at all, so `timeOfDay` is
    // `undefined`, and `sunDirectionAt(undefined)` is `undefined` too. Read
    // literally that is the night treatment, and the first capture of this
    // feature showed exactly what that costs: a flat *dark* cockpit under a
    // bright noon sky, because the sky had already taken this same fallback and
    // the structure had not. `sunDirectionAt` returns nothing for two different
    // reasons and only one of them is night.
    sun: sunDirectionAt(host.timeOfDay ?? DEFAULT_ENHANCED_HOUR),
    // With no muzzle known there is nowhere to put the light, and a flash at the
    // pose origin would wash the whole interior evenly — which reads as the hull
    // changing colour rather than as a gun going off. No muzzle, no flash.
    flashStrength: muzzle ? muzzleFlashStrength(host.muzzleFlash, frame.roundsPerSecond) : 0,
    muzzleWorld: muzzle
      ? { x: frame.pose.position.x + muzzle.x, y: frame.pose.position.y + muzzle.y, z: frame.pose.position.z + muzzle.z }
      : frame.pose.position,
  });
}
